import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Confirm, Dimmer, Icon, Image, Label, Loader, Popup, Segment, TextArea
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { REACTION_EMOJI, TeamPostDto, TeamPostReplyDto, TeamReactionType } from '../../app/models/Team';

const REACTIONS: TeamReactionType[] = ['Like', 'Love', 'Fire', 'Clap', 'Sad'];

// ── ReactionBar ───────────────────────────────────────────────────────────────

interface ReactionBarProps {
  reactions: { type: TeamReactionType; count: number }[];
  currentUserReaction: TeamReactionType | null;
  onReact: (type: TeamReactionType) => void;
}

function ReactionBar({ reactions, currentUserReaction, onReact }: ReactionBarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
      {/* Existing reaction counts */}
      {reactions.map(r => (
        <button
          key={r.type}
          onClick={() => onReact(r.type)}
          style={{
            background: currentUserReaction === r.type ? '#e8f0fe' : '#f5f5f5',
            border: currentUserReaction === r.type ? '1px solid #4285f4' : '1px solid #e0e0e0',
            borderRadius: 12,
            padding: '2px 8px',
            cursor: 'pointer',
            fontSize: '0.82em',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <span>{REACTION_EMOJI[r.type]}</span>
          <span style={{ color: '#555' }}>{r.count}</span>
        </button>
      ))}

      {/* Add reaction button */}
      <Popup
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        trigger={
          <button
            onClick={() => setPickerOpen(v => !v)}
            style={{
              background: 'transparent',
              border: '1px dashed #ccc',
              borderRadius: 12,
              padding: '2px 8px',
              cursor: 'pointer',
              fontSize: '0.82em',
              color: '#888',
            }}
          >
            {currentUserReaction ? REACTION_EMOJI[currentUserReaction] : '＋'}
          </button>
        }
        content={
          <div style={{ display: 'flex', gap: 8, padding: 4 }}>
            {REACTIONS.map(r => (
              <button
                key={r}
                title={r}
                onClick={() => { onReact(r); setPickerOpen(false); }}
                style={{
                  background: currentUserReaction === r ? '#e8f0fe' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.3em',
                  padding: '2px 4px',
                  borderRadius: 6,
                }}
              >
                {REACTION_EMOJI[r]}
              </button>
            ))}
          </div>
        }
        position="top center"
        on="click"
      />
    </div>
  );
}

// ── SingleReply ───────────────────────────────────────────────────────────────

interface SingleReplyProps {
  reply: TeamPostReplyDto;
  postId: string;
}

const SingleReply = observer(function SingleReply({ reply, postId }: SingleReplyProps) {
  const { teamPostStore } = useStore();
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (reply.isDeleted) {
    return (
      <div style={{ padding: '4px 0', color: '#999', fontSize: '0.82em', fontStyle: 'italic' }}>
        <Icon name="trash alternate outline" /> {t('teamChat.deletedMessage')}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
      <Image
        avatar
        src={reply.authorImageUrl || '/assets/user.png'}
        as={Link}
        to={`/profiles/${reply.authorId}`}
        style={{ width: 26, height: 26, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span style={{ fontWeight: 600, fontSize: '0.82em' }}>
            <Link to={`/profiles/${reply.authorId}`}>{reply.authorFullName}</Link>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.75em', color: '#aaa' }}>
              {format(new Date(reply.createdAt), 'dd MMM, HH:mm')}
            </span>
            {reply.canDelete && (
              <Icon
                name="trash alternate"
                size="small"
                color="grey"
                style={{ cursor: 'pointer' }}
                onClick={() => setConfirmDelete(true)}
              />
            )}
          </div>
        </div>
        <p style={{ margin: '2px 0 4px', fontSize: '0.88em' }}>{reply.content}</p>
        <ReactionBar
          reactions={reply.reactions}
          currentUserReaction={reply.currentUserReaction}
          onReact={(type) => teamPostStore.reactToReply(postId, reply.id, type)}
        />
      </div>
      <Confirm
        open={confirmDelete}
        size="mini"
        content={t('teamChat.confirmDeleteReply')}
        cancelButton={t('teams.cancel')}
        confirmButton={<Button negative size="tiny" content={t('teams.remove')} />}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await teamPostStore.deleteReply(postId, reply.id);
          setConfirmDelete(false);
        }}
      />
    </div>
  );
});

// ── SinglePost ────────────────────────────────────────────────────────────────

interface SinglePostProps {
  post: TeamPostDto;
}

const SinglePost = observer(function SinglePost({ post }: SinglePostProps) {
  const { teamPostStore } = useStore();
  const { t } = useTranslation();
  const [replyInput, setReplyInput] = useState('');
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const repliesExpanded = teamPostStore.expandedReplies.has(post.id);

  const handleAddReply = async () => {
    if (!replyInput.trim()) return;
    const ok = await teamPostStore.addReply(post.id, replyInput.trim());
    if (ok) {
      setReplyInput('');
      setShowReplyInput(false);
      if (!repliesExpanded) teamPostStore.toggleReplies(post.id);
    }
  };

  if (post.isDeleted) {
    return (
      <div style={{ padding: '10px 0', color: '#bbb', fontStyle: 'italic', fontSize: '0.88em', borderBottom: '1px solid #f0f0f0' }}>
        <Icon name="trash alternate outline" /> {t('teamChat.deletedMessage')}
      </div>
    );
  }

  return (
    <div style={{ borderBottom: '1px solid #f0f0f0', padding: '12px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Image
          avatar
          src={post.authorImageUrl || '/assets/user.png'}
          as={Link}
          to={`/profiles/${post.authorId}`}
          style={{ width: 36, height: 36, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Link to={`/profiles/${post.authorId}`} style={{ fontWeight: 700, fontSize: '0.9em' }}>
                {post.authorFullName}
              </Link>
              {post.isPinned && (
                <Label size="mini" color="blue">
                  <Icon name="pin" style={{ margin: 0 }} />
                </Label>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.75em', color: '#aaa' }}>
                {format(new Date(post.createdAt), 'dd MMM, HH:mm')}
              </span>
              {/* Actions dropdown */}
              <Popup
                trigger={<Icon name="ellipsis horizontal" color="grey" style={{ cursor: 'pointer' }} />}
                content={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
                    {post.canPin && (
                      <Button
                        size="mini" basic fluid
                        onClick={() => teamPostStore.pinPost(post.id, !post.isPinned)}
                      >
                        <Icon name="pin" />
                        {post.isPinned ? t('teamChat.unpin') : t('teamChat.pin')}
                      </Button>
                    )}
                    {post.canDelete && (
                      <Button size="mini" basic fluid color="red" onClick={() => setConfirmDelete(true)}>
                        <Icon name="trash" />{t('teams.remove')}
                      </Button>
                    )}
                  </div>
                }
                on="click"
                position="bottom right"
              />
            </div>
          </div>

          {/* Content */}
          <p style={{ margin: '6px 0', lineHeight: 1.5, wordBreak: 'break-word' }}>{post.content}</p>

          {/* Reactions */}
          <ReactionBar
            reactions={post.reactions}
            currentUserReaction={post.currentUserReaction}
            onReact={(type) => teamPostStore.reactToPost(post.id, type)}
          />

          {/* Reply toggle */}
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', fontSize: '0.82em', padding: 0 }}
              onClick={() => { setShowReplyInput(v => !v); }}
            >
              <Icon name="reply" />{t('teamChat.reply')}
            </button>
            {post.replyCount > 0 && (
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4285f4', fontSize: '0.82em', padding: 0 }}
                onClick={() => teamPostStore.toggleReplies(post.id)}
              >
                <Icon name={repliesExpanded ? 'angle up' : 'angle down'} />
                {post.replyCount} {t('teamChat.replies')}
              </button>
            )}
          </div>

          {/* Reply input */}
          {showReplyInput && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
              <TextArea
                value={replyInput}
                onChange={(_, d) => setReplyInput(d.value as string)}
                rows={2}
                placeholder={t('teamChat.replyPlaceholder')}
                style={{ flex: 1, resize: 'none', fontSize: '0.88em', padding: 8, borderRadius: 8, border: '1px solid #ddd' }}
              />
              <Button
                size="mini" primary
                disabled={!replyInput.trim() || teamPostStore.submitting}
                loading={teamPostStore.submitting}
                onClick={handleAddReply}
              >
                <Icon name="send" />
              </Button>
            </div>
          )}

          {/* Expanded replies */}
          {repliesExpanded && post.replies.length > 0 && (
            <div style={{ marginTop: 10, paddingLeft: 10, borderLeft: '2px solid #f0f0f0' }}>
              {post.replies.map(r => (
                <SingleReply key={r.id} reply={r} postId={post.id} />
              ))}
            </div>
          )}
        </div>
      </div>

      <Confirm
        open={confirmDelete}
        size="mini"
        content={t('teamChat.confirmDeletePost')}
        cancelButton={t('teams.cancel')}
        confirmButton={<Button negative size="tiny" content={t('teams.remove')} />}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await teamPostStore.deletePost(post.id);
          setConfirmDelete(false);
        }}
      />
    </div>
  );
});

// ── Main TeamChatPanel ────────────────────────────────────────────────────────

interface Props {
  contextType: 'Team' | 'TeamMatch';
  contextId: string;
  isMember: boolean;
}

export default observer(function TeamChatPanel({ contextType, contextId, isMember }: Props) {
  const { teamPostStore } = useStore();
  const { t } = useTranslation();
  const [newPost, setNewPost] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    teamPostStore.loadPosts(contextType, contextId);
    return () => teamPostStore.clearPosts();
  }, [contextType, contextId]);

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setSubmitting(true);
    const ok = await teamPostStore.createPost(contextType, contextId, newPost.trim());
    setSubmitting(false);
    if (ok) setNewPost('');
  };

  return (
    <Segment style={{ padding: 0, border: 'none', boxShadow: 'none' }}>
      {/* New post input */}
      {isMember && (
        <div style={{
          display: 'flex', gap: 10, padding: '12px 0 16px',
          borderBottom: '2px solid #f0f0f0', marginBottom: 8
        }}>
          <div style={{ flex: 1 }}>
            <textarea
              ref={textRef}
              value={newPost}
              onChange={e => setNewPost(e.target.value)}
              placeholder={t('teamChat.writeSomething')}
              rows={2}
              style={{
                width: '100%', resize: 'none', borderRadius: 10, border: '1px solid #ddd',
                padding: '10px 12px', fontSize: '0.92em', outline: 'none', fontFamily: 'inherit'
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handlePost();
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
              <Button
                primary size="small" disabled={!newPost.trim() || submitting} loading={submitting}
                onClick={handlePost} style={{ borderRadius: 20 }}
              >
                <Icon name="send" />{t('teamChat.post')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Posts list */}
      {teamPostStore.loading ? (
        <Dimmer active inverted style={{ minHeight: 100 }}>
          <Loader inverted>{t('teams.loading')}</Loader>
        </Dimmer>
      ) : teamPostStore.posts.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '30px 0' }}>
          <Icon name="comments" size="large" />
          <p>{isMember ? t('teamChat.noPostsYet') : t('teamChat.notMemberNotice')}</p>
        </div>
      ) : (
        teamPostStore.posts.map(post => (
          <SinglePost key={post.id} post={post} />
        ))
      )}
    </Segment>
  );
});